import React, { useState, useRef, useEffect, useCallback } from 'react';

export interface Country {
  name: string;
  code: string;   // ISO 2-letter
  dial: string;   // e.g. "+238"
}

// Flag emoji from ISO country code
function flagEmoji(code: string): string {
  return code.toUpperCase().replace(/./g, c =>
    String.fromCodePoint(c.charCodeAt(0) - 65 + 0x1F1E6)
  );
}

// Ordered: CV first, then PT-speaking Africa, then rest of Africa, Europe, Americas, rest
export const COUNTRIES: Country[] = [
  { name: 'Cabo Verde', code: 'CV', dial: '+238' },
  { name: 'Angola', code: 'AO', dial: '+244' },
  { name: 'Moçambique', code: 'MZ', dial: '+258' },
  { name: 'Guiné-Bissau', code: 'GW', dial: '+245' },
  { name: 'São Tomé e Príncipe', code: 'ST', dial: '+239' },
  { name: 'Portugal', code: 'PT', dial: '+351' },
  { name: 'Brasil', code: 'BR', dial: '+55' },
  { name: 'Senegal', code: 'SN', dial: '+221' },
  { name: 'Mauritânia', code: 'MR', dial: '+222' },
  { name: 'Guiné-Conacri', code: 'GN', dial: '+224' },
  { name: 'Gâmbia', code: 'GM', dial: '+220' },
  { name: 'Serra Leoa', code: 'SL', dial: '+232' },
  { name: 'Libéria', code: 'LR', dial: '+231' },
  { name: 'Mali', code: 'ML', dial: '+223' },
  { name: 'Burkina Faso', code: 'BF', dial: '+226' },
  { name: 'Costa do Marfim', code: 'CI', dial: '+225' },
  { name: 'Gana', code: 'GH', dial: '+233' },
  { name: 'Togo', code: 'TG', dial: '+228' },
  { name: 'Benin', code: 'BJ', dial: '+229' },
  { name: 'Nigéria', code: 'NG', dial: '+234' },
  { name: 'Níger', code: 'NE', dial: '+227' },
  { name: 'Camarões', code: 'CM', dial: '+237' },
  { name: 'Guiné Equatorial', code: 'GQ', dial: '+240' },
  { name: 'Gabão', code: 'GA', dial: '+241' },
  { name: 'Congo', code: 'CG', dial: '+242' },
  { name: 'RD Congo', code: 'CD', dial: '+243' },
  { name: 'Chade', code: 'TD', dial: '+235' },
  { name: 'Etiópia', code: 'ET', dial: '+251' },
  { name: 'Quénia', code: 'KE', dial: '+254' },
  { name: 'Tanzânia', code: 'TZ', dial: '+255' },
  { name: 'Uganda', code: 'UG', dial: '+256' },
  { name: 'Ruanda', code: 'RW', dial: '+250' },
  { name: 'África do Sul', code: 'ZA', dial: '+27' },
  { name: 'Zimbabwe', code: 'ZW', dial: '+263' },
  { name: 'Namíbia', code: 'NA', dial: '+264' },
  { name: 'Botswana', code: 'BW', dial: '+267' },
  { name: 'Marrocos', code: 'MA', dial: '+212' },
  { name: 'Argélia', code: 'DZ', dial: '+213' },
  { name: 'Tunísia', code: 'TN', dial: '+216' },
  { name: 'Líbia', code: 'LY', dial: '+218' },
  { name: 'Egito', code: 'EG', dial: '+20' },
  { name: 'Espanha', code: 'ES', dial: '+34' },
  { name: 'França', code: 'FR', dial: '+33' },
  { name: 'Reino Unido', code: 'GB', dial: '+44' },
  { name: 'Alemanha', code: 'DE', dial: '+49' },
  { name: 'Itália', code: 'IT', dial: '+39' },
  { name: 'Países Baixos', code: 'NL', dial: '+31' },
  { name: 'Bélgica', code: 'BE', dial: '+32' },
  { name: 'Suíça', code: 'CH', dial: '+41' },
  { name: 'Suécia', code: 'SE', dial: '+46' },
  { name: 'Noruega', code: 'NO', dial: '+47' },
  { name: 'Dinamarca', code: 'DK', dial: '+45' },
  { name: 'Finlândia', code: 'FI', dial: '+358' },
  { name: 'Polónia', code: 'PL', dial: '+48' },
  { name: 'EUA', code: 'US', dial: '+1' },
  { name: 'Canadá', code: 'CA', dial: '+1' },
  { name: 'México', code: 'MX', dial: '+52' },
  { name: 'Argentina', code: 'AR', dial: '+54' },
  { name: 'Colômbia', code: 'CO', dial: '+57' },
  { name: 'Venezuela', code: 'VE', dial: '+58' },
  { name: 'Peru', code: 'PE', dial: '+51' },
  { name: 'Chile', code: 'CL', dial: '+56' },
  { name: 'China', code: 'CN', dial: '+86' },
  { name: 'Índia', code: 'IN', dial: '+91' },
  { name: 'Japão', code: 'JP', dial: '+81' },
  { name: 'Coreia do Sul', code: 'KR', dial: '+82' },
  { name: 'Emirados Árabes', code: 'AE', dial: '+971' },
  { name: 'Arábia Saudita', code: 'SA', dial: '+966' },
  { name: 'Turquia', code: 'TR', dial: '+90' },
  { name: 'Rússia', code: 'RU', dial: '+7' },
  { name: 'Austrália', code: 'AU', dial: '+61' },
];

interface PhoneInputProps {
  value: string;               // full number e.g. "+238 999 9999"
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  error?: boolean;
}

const PhoneInput: React.FC<PhoneInputProps> = ({
  value,
  onChange,
  placeholder,
  className = '',
  error = false,
}) => {
  // Detect initial country from value
  const detectCountry = useCallback((v: string): Country => {
    if (v) {
      // Try to match dial code from longest to shortest
      const sorted = [...COUNTRIES].sort((a, b) => b.dial.length - a.dial.length);
      for (const c of sorted) {
        if (v.startsWith(c.dial)) return c;
      }
    }
    return COUNTRIES[0]; // default: CV
  }, []);

  const [selected, setSelected] = useState<Country>(() => detectCountry(value));
  const [local, setLocal] = useState<string>(() => {
    if (value && value.startsWith(selected.dial)) {
      return value.slice(selected.dial.length).trim();
    }
    return value || '';
  });
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Focus search when dropdown opens
  useEffect(() => {
    if (open) searchRef.current?.focus();
  }, [open]);

  const handleCountrySelect = (country: Country) => {
    setSelected(country);
    setOpen(false);
    setSearch('');
    const full = local.trim() ? `${country.dial} ${local.trim()}` : '';
    onChange(full);
  };

  const handleLocalChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Allow digits, spaces, hyphens, parentheses only
    const v = e.target.value.replace(/[^\d\s\-()]/g, '');
    setLocal(v);
    const full = v.trim() ? `${selected.dial} ${v.trim()}` : '';
    onChange(full);
  };

  const filtered = COUNTRIES.filter(c =>
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    c.dial.includes(search) ||
    c.code.toLowerCase().includes(search.toLowerCase())
  );

  const borderClass = error
    ? 'border-red-500'
    : 'border-gray-300 focus-within:border-pagali-primary focus-within:ring-2 focus-within:ring-pagali-primary/20';

  return (
    <div className={`relative flex rounded-lg border ${borderClass} overflow-visible bg-white transition-all ${className}`} ref={dropdownRef}>
      {/* Country selector button */}
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-1.5 px-3 py-3 border-r border-gray-200 bg-gray-50 hover:bg-gray-100 transition-colors rounded-l-lg shrink-0 focus:outline-none"
        aria-label="Selecionar indicativo"
      >
        <span className="text-xl leading-none">{flagEmoji(selected.code)}</span>
        <span className="text-sm text-gray-700 font-medium">{selected.dial}</span>
        <svg
          className={`w-3.5 h-3.5 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`}
          fill="none" stroke="currentColor" viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Local number input */}
      <input
        type="tel"
        value={local}
        onChange={handleLocalChange}
        placeholder={placeholder || '999 9999'}
        className="flex-1 px-3 py-3 text-sm bg-transparent focus:outline-none rounded-r-lg min-w-0"
        autoComplete="tel-local"
      />

      {/* Dropdown */}
      {open && (
        <div className="absolute left-0 top-full mt-1 w-72 bg-white border border-gray-200 rounded-xl shadow-xl z-50 overflow-hidden">
          {/* Search */}
          <div className="p-2 border-b border-gray-100">
            <input
              ref={searchRef}
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Pesquisar país..."
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-pagali-primary/20 focus:border-pagali-primary"
            />
          </div>

          {/* List */}
          <ul className="max-h-60 overflow-y-auto">
            {filtered.length === 0 ? (
              <li className="px-4 py-3 text-sm text-gray-400 text-center">Sem resultados</li>
            ) : (
              filtered.map(country => (
                <li key={`${country.code}-${country.dial}`}>
                  <button
                    type="button"
                    onClick={() => handleCountrySelect(country)}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors text-left ${
                      selected.code === country.code && selected.dial === country.dial
                        ? 'bg-pagali-primary/5 text-pagali-primary font-medium'
                        : 'text-gray-700'
                    }`}
                  >
                    <span className="text-xl leading-none w-7 text-center">{flagEmoji(country.code)}</span>
                    <span className="flex-1 truncate">{country.name}</span>
                    <span className="text-gray-400 text-xs shrink-0">{country.dial}</span>
                  </button>
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default PhoneInput;
